import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type {
  ChangeGradingMode,
  ChangeIntentView,
  ChangeVerdictView,
  ChangeVerdictKind,
  ChangeWindow,
  ChangeWindowInput,
} from "@detect/shared";
import { changeService } from "../../services/changes";
import { resourceService } from "../../services/resources";
import { clientService } from "../../services/clients";
import { collectorService } from "../../services/collectors";
import { tenantService } from "../../services/tenants";
import type { WithId } from "../../services/types";
import { useTenant } from "../../contexts/TenantContext";
import { useCursorList } from "../../hooks/useCursorList";
import { usePickerOptions } from "../../hooks/usePickerOptions";
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from "../../components/common";
import { formatDateTime } from "../../utils/formatters";
import {
  describeBaseline,
  describeCriterion,
  describeIntent,
  draftsToCriteria,
  emptyCriterionDraft,
  GRADING_MODE_LABELS,
  type CriterionDraft,
  describeScope,
  draftsToScopeRefs,
  VERDICT_LIMIT,
  VERDICT_EXPLANATIONS,
  VERDICT_LABELS,
  type ScopeDraft,
} from "./change-helpers";
import { collectorOptionLabel } from "../collectors/channel";

/**
 * Change windows (#26; change-participation.md). Expected-impact entities are
 * suppressed during the window and verified after it; witnesses are never
 * suppressed and must stay unaffected. Windows are immutable (create/delete).
 * This UI creates ONE-TIME windows; recurring windows are supported by the API.
 */
export function ChangesTab() {
  const { clientScope } = useTenant();
  // Change windows are tenant-wide (the API refuses client-scoped users): say so rather than
  // offering controls that can only fail.
  if (clientScope !== null) {
    return (
      <EmptyState
        title="Change windows are tenant-wide"
        description="They suppress and verify across clients, so they are not available to users limited to specific clients."
      />
    );
  }
  return <ChangesList />;
}

/** id → name for every resource, client and collector in the tenant (the pick lists, paged in full). */
function useScopeNameLookup(): ReadonlyMap<string, string> {
  const resources = usePickerOptions(["resources"], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter }),
  );
  const clients = usePickerOptions(["clients"], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter }),
  );
  const collectors = usePickerOptions(["collectors"], (tid, startAfter) =>
    collectorService.list(tid, { limit: 100, startAfter }),
  );
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const r of resources.data?.items ?? []) m.set(r.id, r.displayName ?? r.name);
    for (const c of clients.data?.items ?? []) m.set(c.id, c.name);
    for (const c of collectors.data?.items ?? []) m.set(c.id, c.name);
    return m;
  }, [resources.data, clients.data, collectors.data]);
}

function ChangesList() {
  const { tenantId, hasRole } = useTenant();
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<WithId<ChangeWindow> | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  // Deleted changes are history, not erased: they stay readable (their results included), read-only (#82).
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      changeService.list(tid, { limit: 50, startAfter, deleted: showDeleted }),
    [showDeleted],
  );
  const list = useCursorList<WithId<ChangeWindow>>(
    fetchPage,
    showDeleted ? "deleted" : "all",
  );
  // Resource, client and collector scopes are shown by name, never by internal id.
  const scopeNames = useScopeNameLookup();

  const remove = async (w: WithId<ChangeWindow>) => {
    if (!tenantId) return;
    if (
      !window.confirm(
        "Delete this change window? Its suppression ends now, and an occurrence that has not finished is recorded as abandoned.",
      )
    )
      return;
    setActionError(null);
    try {
      await changeService.remove(tenantId, w.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {hasRole("agent") && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(true)}
          >
            Plan a change
          </button>
        )}
      </div>
      <label className="form-check">
        <input
          type="checkbox"
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}
        />{" "}
        Show deleted changes
      </label>
      {actionError !== null && <ErrorState error={actionError} />}
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title={showDeleted ? "No deleted changes" : "No change windows"}
          description={
            showDeleted
              ? "Changes you delete are kept here, with their results, as a record."
              : "Plan a change to suppress expected impact during it and check afterwards that everything came back."
          }
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Change</th>
                <th>Window</th>
                <th>Expected impact</th>
                <th>Witnesses</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.items.map((w) => (
                <tr key={w.id}>
                  <td>
                    {w.title}
                    {w.deletedAt ? (
                      <div className="text-muted">
                        Deleted {formatDateTime(w.deletedAt)}
                      </div>
                    ) : null}
                    {w.comment ? (
                      <div className="text-muted">{w.comment}</div>
                    ) : null}
                  </td>
                  <td>
                    {w.startsAt && w.endsAt
                      ? `${formatDateTime(w.startsAt)} → ${formatDateTime(w.endsAt)}`
                      : "Recurring"}
                  </td>
                  <td>
                    {w.expectedImpact
                      .map((r) => describeScope(r, scopeNames))
                      .join(", ")}
                  </td>
                  <td>
                    {w.witnesses.length
                      ? w.witnesses
                          .map((r) => describeScope(r, scopeNames))
                          .join(", ")
                      : "—"}
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setViewing(w)}
                    >
                      Results
                    </button>
                    {hasRole("agent") && !w.deletedAt && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(w)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {creating && (
        <ChangeModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void list.reload();
          }}
        />
      )}
      {viewing && (
        <ResultsModal window_={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

const VERDICT_CLASS: Record<ChangeVerdictKind, string> = {
  clean: "status-active",
  "did-not-recover": "status-down",
  "got-worse": "status-down",
  "blast-radius": "status-down",
  indeterminate: "status-pending",
  abandoned: "status-disabled",
};

function ResultsModal({
  window_,
  onClose,
}: {
  window_: WithId<ChangeWindow>;
  onClose: () => void;
}) {
  const { tenantId } = useTenant();
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error"; error: unknown }
    | { status: "ok"; items: ChangeVerdictView[] }
  >({ status: "loading" });
  const load = useCallback(() => {
    if (!tenantId) return;
    setState({ status: "loading" });
    changeService.verdicts(tenantId, window_.id).then(
      (items) => setState({ status: "ok", items }),
      (error: unknown) => setState({ status: "error", error }),
    );
  }, [tenantId, window_.id]);
  useEffect(() => {
    load();
  }, [load]);
  const scopeNames = useScopeNameLookup();
  const resourceName = useCallback((id: string) => scopeNames.get(id) ?? "(removed)", [scopeNames]);

  return (
    <Modal title={`Results — ${window_.title}`} onClose={onClose} wide>
      {window_.deletedAt && (
        <p className="text-muted">
          This change was deleted on {formatDateTime(window_.deletedAt)}. Its
          results are kept as a record and are read-only.
        </p>
      )}
      {window_.intent && window_.intent.criteria.length > 0 && (
        <div className="text-muted">
          Success criteria (all must hold):
          <ul>
            {window_.intent.criteria.map((c, i) => (
              <li key={i}>{describeCriterion(c, resourceName)}</li>
            ))}
          </ul>
        </div>
      )}
      {state.status === "loading" && <LoadingSpinner />}
      {state.status === "error" && (
        <ErrorState error={state.error} onRetry={load} />
      )}
      {state.status === "ok" &&
        (state.items.length === 0 ? (
          <EmptyState
            title="No verdict yet"
            description="A verdict is recorded once the window has ended and fresh data has arrived."
          />
        ) : (
          <>
            {state.items.length >= VERDICT_LIMIT && (
              <p className="text-muted">
                Showing the newest {VERDICT_LIMIT} occurrences; older results
                are not listed.
              </p>
            )}
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Occurrence</th>
                    <th>Verdict</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {state.items.map((v) => (
                    <tr key={v.id}>
                      <td>
                        {formatDateTime(v.occurrenceStartsAt)} →{" "}
                        {formatDateTime(v.occurrenceEndsAt)}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${VERDICT_CLASS[v.verdict]}`}
                        >
                          {VERDICT_LABELS[v.verdict]}
                        </span>
                      </td>
                      <td>
                        {VERDICT_EXPLANATIONS[v.verdict]}
                        {v.indeterminate && (
                          <div className="text-muted">
                            Reason: {v.indeterminate.reason}
                          </div>
                        )}
                        {v.findings.length > 0 && (
                          <div className="text-muted">
                            {v.findings.length}
                            {v.findingsTruncated ? "+" : ""} finding(s):{" "}
                            {
                              v.findings.filter((f) => f.role === "witness")
                                .length
                            }{" "}
                            on witnesses
                          </div>
                        )}
                        {v.returnedToOriginal && (
                          <div className="text-muted">
                            {v.returnedToOriginal.identical
                              ? "Returned to the original state."
                              : `${v.returnedToOriginal.improved} improved, ${v.returnedToOriginal.unchanged} unchanged.`}
                          </div>
                        )}
                        <IntentBlock
                          windowId={window_.id}
                          intent={v.intent}
                          occurrenceStartsAt={v.occurrenceStartsAt}
                          onMarked={load}
                        />
                        {(() => {
                          const base = describeBaseline(v, resourceName);
                          if (!base) return null;
                          return (
                            <div className="text-muted">
                              {base.summary}
                              {base.lines.length > 0 && (
                                <ul>
                                  {base.lines.map((l) => (
                                    <li key={l}>{l}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ))}
    </Modal>
  );
}

function ScopeRows({
  legend,
  rows,
  setRows,
  minRows,
  names,
}: {
  legend: string;
  rows: ScopeDraft[];
  setRows: (r: ScopeDraft[]) => void;
  minRows: number;
  names: {
    resources: { id: string; label: string }[];
    clients: { id: string; label: string }[];
    collectors: { id: string; label: string }[];
  };
}) {
  const update = (i: number, patch: Partial<ScopeDraft>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="form-field">
      <legend>{legend}</legend>
      {rows.map((r, i) => {
        const options =
          r.scopeType === "resource"
            ? names.resources
            : r.scopeType === "client"
              ? names.clients
              : r.scopeType === "collector"
                ? names.collectors
                : [];
        return (
          <div key={i} className="inline-row">
            <select
              aria-label={`${legend} ${i + 1} scope`}
              value={r.scopeType}
              onChange={(e) =>
                update(i, {
                  scopeType: e.target.value as ScopeDraft["scopeType"],
                  targetId: "",
                })
              }
            >
              <option value="resource">Resource</option>
              <option value="client">Client</option>
              <option value="collector">Collector</option>
              <option value="tenant">Whole tenant</option>
            </select>
            {r.scopeType !== "tenant" && (
              <select
                aria-label={`${legend} ${i + 1} target`}
                required
                value={r.targetId}
                onChange={(e) => update(i, { targetId: e.target.value })}
              >
                <option value="">— select —</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            {rows.length > minRows && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() =>
          setRows([...rows, { scopeType: "resource", targetId: "" }])
        }
      >
        Add
      </button>
    </fieldset>
  );
}

function IntentBlock({
  windowId,
  intent,
  occurrenceStartsAt,
  onMarked,
}: {
  windowId: string;
  intent: ChangeIntentView;
  occurrenceStartsAt: string;
  onMarked: () => void;
}) {
  const { tenantId } = useTenant();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const described = describeIntent(intent);
  const mark = async (result: "achieved" | "not_achieved") => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await changeService.mark(tenantId, windowId, {
        occurrenceStartsAt,
        result,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setNote("");
      onMarked();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="text-muted">
      {described && (
        <>
          {described.summary}
          {described.lines.length > 0 && (
            <ul>
              {described.lines.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {error !== null && <ErrorState error={error} />}
      <div className="inline-row">
        <input
          aria-label="Note for your mark (optional)"
          placeholder="Note (optional)"
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy}
          onClick={() => void mark("achieved")}
        >
          Mark achieved
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy}
          onClick={() => void mark("not_achieved")}
        >
          Mark not achieved
        </button>
      </div>
    </div>
  );
}

function CriteriaRows({
  rows,
  setRows,
  resources,
}: {
  rows: CriterionDraft[];
  setRows: (r: CriterionDraft[]) => void;
  resources: { id: string; label: string }[];
}) {
  const update = (i: number, patch: Partial<CriterionDraft>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="form-field">
      <legend>Success criteria (all must hold)</legend>
      {rows.map((r, i) => (
        <div key={i} className="inline-row">
          <select
            aria-label={`Criterion ${i + 1} kind`}
            value={r.kind}
            onChange={(e) =>
              update(i, { kind: e.target.value as CriterionDraft["kind"] })
            }
          >
            <option value="metric">A metric is below / above a value</option>
            <option value="alert-cleared">An alert is cleared and stays cleared</option>
            <option value="sensor-ok">A sensor is ok</option>
          </select>
          <select
            aria-label={`Criterion ${i + 1} resource`}
            required
            value={r.resourceId}
            onChange={(e) => update(i, { resourceId: e.target.value })}
          >
            <option value="">— resource —</option>
            {resources.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            aria-label={`Criterion ${i + 1} sensor key`}
            placeholder="sensor key, e.g. ping"
            value={r.sensorKey}
            onChange={(e) => update(i, { sensorKey: e.target.value })}
          />
          <input
            aria-label={`Criterion ${i + 1} instance`}
            placeholder="instance (optional)"
            value={r.instanceId}
            onChange={(e) => update(i, { instanceId: e.target.value })}
          />
          {r.kind !== "sensor-ok" && (
            <input
              aria-label={`Criterion ${i + 1} datapoint`}
              placeholder="datapoint, e.g. rtt"
              value={r.datapoint}
              onChange={(e) => update(i, { datapoint: e.target.value })}
            />
          )}
          {r.kind === "metric" && (
            <>
              <select
                aria-label={`Criterion ${i + 1} comparator`}
                value={r.comparator}
                onChange={(e) =>
                  update(i, {
                    comparator: e.target.value as CriterionDraft["comparator"],
                  })
                }
              >
                <option value="below">below</option>
                <option value="above">above</option>
              </select>
              <input
                aria-label={`Criterion ${i + 1} value`}
                inputMode="decimal"
                placeholder="value"
                value={r.threshold}
                onChange={(e) => update(i, { threshold: e.target.value })}
              />
            </>
          )}
          <input
            aria-label={`Criterion ${i + 1} minutes after the window`}
            inputMode="numeric"
            placeholder="minutes after the window"
            value={r.minutes}
            onChange={(e) => update(i, { minutes: e.target.value })}
          />
          {rows.length > 1 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setRows(rows.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => setRows([...rows, emptyCriterionDraft()])}
      >
        Add criterion
      </button>
    </fieldset>
  );
}

function ChangeModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId, clientScope } = useTenant();
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [expected, setExpected] = useState<ScopeDraft[]>([
    { scopeType: "resource", targetId: "" },
  ]);
  const [witnesses, setWitnesses] = useState<ScopeDraft[]>([]);
  const [gradingMode, setGradingMode] = useState<ChangeGradingMode>("none");
  const [criteria, setCriteria] = useState<CriterionDraft[]>([
    emptyCriterionDraft(),
  ]);
  const [successText, setSuccessText] = useState("");
  /** Has an account admin switched AI analysis on (Settings)? Unknown or unreadable reads as off. */
  const [aiOn, setAiOn] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let live = true;
    tenantService.current(tenantId).then(
      (t) => live && setAiOn(t.ai?.changeIntentAnalysis === true),
      () => live && setAiOn(false),
    );
    return () => {
      live = false;
    };
  }, [tenantId]);

  const resourcesQuery = usePickerOptions(["resources"], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter }),
  );
  const clientsQuery = usePickerOptions(["clients"], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter }),
  );
  const collectorsQuery = usePickerOptions(["collectors"], (tid, startAfter) =>
    collectorService.list(tid, { limit: 100, startAfter }),
  );
  const names = {
    resources: (resourcesQuery.data?.items ?? []).map((r) => ({
      id: r.id,
      label: r.displayName ?? r.name,
    })),
    clients: (clientsQuery.data?.items ?? [])
      .filter((c) => clientScope === null || clientScope.includes(c.id))
      .map((c) => ({ id: c.id, label: c.name })),
    collectors: (collectorsQuery.data?.items ?? []).map((c) => ({
      id: c.id,
      label: collectorOptionLabel(c),
    })),
  };
  const truncated =
    resourcesQuery.data?.truncated ||
    clientsQuery.data?.truncated ||
    collectorsQuery.data?.truncated;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: ChangeWindowInput = {
        title,
        ...(comment ? { comment } : {}),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        expectedImpact: draftsToScopeRefs(expected),
        witnesses: draftsToScopeRefs(witnesses),
        // Rung 3: a window that says how success is judged carries an intent (Detect-authored, one per submit).
        ...(gradingMode !== "none"
          ? {
              intent: {
                externalRef: `detect-${globalThis.crypto.randomUUID()}`,
                source: "detect" as const,
                purpose: "remediation" as const,
                ...(successText.trim()
                  ? { successCriteria: successText.trim() }
                  : {}),
                gradingMode,
                criteria:
                  gradingMode === "structured"
                    ? draftsToCriteria(criteria)
                    : [],
              },
            }
          : {}),
      };
      await changeService.create(tenantId, input);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Plan a change" onClose={onClose} wide>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="chg-title">Title</label>
          <input
            id="chg-title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="chg-start">Starts</label>
          <input
            id="chg-start"
            type="datetime-local"
            required
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="chg-end">Ends</label>
          <input
            id="chg-end"
            type="datetime-local"
            required
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        <ScopeRows
          legend="Expected impact (suppressed, then verified)"
          rows={expected}
          setRows={setExpected}
          minRows={1}
          names={names}
        />
        <ScopeRows
          legend="Witnesses (must stay unaffected)"
          rows={witnesses}
          setRows={setWitnesses}
          minRows={0}
          names={names}
        />
        {truncated && (
          <p className="text-muted">Some pick lists are truncated.</p>
        )}
        <div className="form-field">
          <label htmlFor="chg-grading">How will success be judged?</label>
          <select
            id="chg-grading"
            value={gradingMode}
            onChange={(e) => setGradingMode(e.target.value as ChangeGradingMode)}
          >
            {(["none", "structured", "human", "ai"] as const).map((m) => (
              <option key={m} value={m} disabled={m === "ai" && !aiOn}>
                {GRADING_MODE_LABELS[m]}
                {m === "ai" && !aiOn ? " (off: an account admin can turn it on in Settings)" : ""}
              </option>
            ))}
          </select>
          {gradingMode === "ai" && (
            <p className="text-muted">
              After the window, Detect sends this change&apos;s title, purpose and success text, and a short before/after
              summary of its resources&apos; metrics and alerts, to Gemini on Google Vertex AI, and shows the answer
              beside the verdict. It is advisory: check it, and mark the result yourself if it is wrong.
            </p>
          )}
          <p className="text-muted">
            Advisory: it is shown beside the verdict and never changes it. A
            person can always record a mark afterwards.
          </p>
        </div>
        {gradingMode !== "none" && (
          <div className="form-field">
            <label htmlFor="chg-success">What does success mean?</label>
            <input
              id="chg-success"
              value={successText}
              maxLength={4000}
              onChange={(e) => setSuccessText(e.target.value)}
            />
          </div>
        )}
        {gradingMode === "structured" && (
          <CriteriaRows
            rows={criteria}
            setRows={setCriteria}
            resources={names.resources}
          />
        )}
        <div className="form-field">
          <label htmlFor="chg-comment">Comment</label>
          <input
            id="chg-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Planning…" : "Plan change"}
        </button>
      </form>
    </Modal>
  );
}
